import { Button } from '@velocity/ui';
import { motion } from 'framer-motion';
import ls from 'localstorage-slim';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setIsAnimated } from '../../../redux/cache-slice';
import { fetchCarDetails } from '../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { Partner, Type } from '../../../types/instant-lease-api';
import UnknownCar from '../../b2b/components/images/unknown-car.png';
import HeaderBar from '../header/header-bar';
import SubHeaderBand from '../sub-header/sub-header-band';
import VehicleOverview from '../vehicle-overview/vehicle-overview';

export interface FlexCarDetails {
  duration: string;
  gross: number;
  mileage: number;
  net: number;
  title: string;
  vehicleImageUrl: string;
  type: Type.B2C | Type.B2B;
}

const Bootstrap: React.FC = () => {
  // Lease Application Storage
  const { carData } = useSelector((state: RootState) => state.leaseApp);

  const [isExiting, setIsExiting] = useState(false);
  const [loadingCarDetailError, setLoadingCarDetailError] =
    useState<Error | null>(null);

  // History hook
  const navigate = useNavigate();

  // Dispatch hook
  const dispatch = useAppDispatch();

  const { pathname } = useLocation();

  // Current URL
  const searchParams = new URLSearchParams(document.location.search);

  // Get previous URL from browser history
  const mobilityPortalUrl =
    window.history.length > 1 ? document.referrer : null;

  //Save the previous URL to local storage for later use
  useEffect(() => {
    if (mobilityPortalUrl) {
      ls.set('mobilityPortalUrl', mobilityPortalUrl);
    }
  }, [mobilityPortalUrl]);

  // URL Variables
  const partner = Partner.valueOf(searchParams.get('partner')?.toUpperCase());
  const publicationId = searchParams.get('publicationId');
  const pricingId = searchParams.get('pricingId');
  const offerId = searchParams.get('offerId');

  // if no offer or carData move to no offer page
  useEffect(() => {
    if (!publicationId && !pricingId && !offerId) {
      navigate('/login');
    }
  }, [partner, offerId, pricingId, publicationId, navigate]);

  // Get current car regardless of saved data
  useEffect(() => {
    const fetchCurrentCarDetails = async () => {
      try {
        await dispatch(
          fetchCarDetails({ partner, publicationId, pricingId, offerId }),
        ).unwrap();
      } catch (error) {
        console.error('Error fetching car details for bootstrap page:', error);
        setLoadingCarDetailError(
          error instanceof Error
            ? error
            : new Error('Error fetching car details for bootstrap page'),
        );
      }
    };

    if ((publicationId && pricingId) || offerId) {
      ls.set('bootstrapPage', true);
      fetchCurrentCarDetails();
    }
  }, [partner, offerId, pricingId, publicationId, dispatch, pathname]);

  const handleContinue = () => {
    setIsExiting(true);
    dispatch(setIsAnimated(true));

    // Wait for the exit animation + a tiny buffer
    setTimeout(() => {
      navigate('/login');
    }, 450); // matches motion.div transition (0.4s)
  };

  // If the car details fail to load, navigate to the error page.
  if (loadingCarDetailError) {
    console.error(
      'Error during loading of car details, navigating to error page',
    );
    navigate('/invalid-offer-error');
  }

  return (
    <motion.div
      initial={{ opacity: 1, y: 0 }}
      animate={isExiting ? { opacity: 0, y: -40 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
      <HeaderBar />
      {carData && <SubHeaderBand />}
      <div className='bootstrap grid-template container'>
        <div className='template-body-wide'>
          {carData && (
            <div className='w-full pt-6 pb-16 md:pt-8 lg:pt-10'>
              <VehicleOverview
                fetchOnMount={false}
                showCancelButton={false}
                action={
                  <Button
                    variant='secondary-filled'
                    size='l'
                    fullWidth
                    onClick={() => {
                      window.scrollTo({
                        top: 0,
                        left: 0,
                        behavior: 'smooth',
                      });
                      handleContinue();
                      navigate('/login');
                    }}
                  >
                    Weiter
                  </Button>
                }
              />
            </div>
          )}

          {!carData && (
            <img src={UnknownCar} alt='car' className='car-list unknown-car' />
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Bootstrap;
